import useSWR from "swr";
import { MondayApiQueryInstance } from "../../services/monday/monday-service-ts";
import logger from "../../lib/logger";
import {
  splitSharedColumns,
  type BoardColumn,
  type SharedColumnsSplit,
} from "../../utils/sharedColumns";

type BoardWithColumns = { id: string | number; columns?: BoardColumn[] | null };

async function fetchSharedColumns(originBoardId: number, targetBoardId: number) {
  const boards: BoardWithColumns[] | undefined = await MondayApiQueryInstance.getBoardAllColumns([
    originBoardId,
    targetBoardId,
  ]);
  const columnsOf = (boardId: number) =>
    boards?.find((board) => String(board.id) === String(boardId))?.columns;

  const origin = columnsOf(originBoardId);
  const target = columnsOf(targetBoardId);
  // monday leaves out a board the user can't read, so an empty answer is not "no columns".
  if (!origin || !target) throw new Error("Board columns missing from the monday response");

  return splitSharedColumns(origin, target);
}

/**
 * Which of the origin board's columns will sync to the target board, for the add-item preview.
 * Reads both boards' columns in a single query.
 */
export function useSharedColumnsPreview(
  originBoardId: number | null | undefined,
  targetBoardId: number | string | null | undefined,
) {
  const target = targetBoardId != null ? Number(targetBoardId) : null;
  const key = originBoardId && target ? `shared-columns-preview:${originBoardId}:${target}` : null;

  const { data, error, isLoading, mutate } = useSWR<SharedColumnsSplit>(
    key,
    () => fetchSharedColumns(originBoardId!, target!),
    {
      revalidateOnFocus: false,
      dedupingInterval: 60_000,
      onError: (err) =>
        logger.error("Error loading shared columns preview", {
          error: err,
          originBoardId,
          targetBoardId: target,
        }),
    },
  );

  return { split: data, error, isLoading, retry: () => mutate() };
}
